import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const DownloadActionPanel = ({ 
  projectName = 'My Project',
  projectSize = '2.4 MB',
  fileCount = 42,
  onDownload,
  onClose,
  isVisible = true 
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [showModal, setShowModal] = useState(false);

  const handleDownloadClick = () => {
    setShowModal(true);
  };

  const handleConfirmDownload = async () => {
    setIsDownloading(true);
    setDownloadProgress(0);

    const progressInterval = setInterval(() => {
      setDownloadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 10;
      });
    }, 200);

    try {
      if (onDownload) {
        await onDownload();
      }
      
      setTimeout(() => {
        setIsDownloading(false);
        setShowModal(false);
        setDownloadProgress(0);
      }, 2500);
    } catch (error) {
      console.error('Download failed:', error);
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  const handleCloseModal = () => {
    if (!isDownloading) {
      setShowModal(false);
      setDownloadProgress(0);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <div className="download-panel animate-scale-in">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
              <Icon name="Package" size={24} color="#FFFFFF" />
            </div>
            <div>
              <h4 className="font-semibold">{projectName}</h4>
              <p className="text-sm text-muted-foreground">
                {fileCount} files • {projectSize}
              </p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-muted rounded-md transition-colors"
              aria-label="Close panel"
            >
              <Icon name="X" size={20} />
            </button>
          )}
        </div>

        <div className="space-y-3">
          <Button
            variant="default"
            fullWidth
            iconName="Download"
            iconPosition="left"
            onClick={handleDownloadClick}
          >
            Download Project
          </Button>
          <Button
            variant="outline"
            fullWidth
            iconName="Eye"
            iconPosition="left"
          >
            Preview Files
          </Button>
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Info" size={16} />
            <span>Ready to download as ZIP</span>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="download-modal-overlay animate-fade-in">
          <div className="download-modal-content animate-scale-in">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
                  <Icon name="Download" size={24} color="#FFFFFF" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Download Project</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {projectName}
                  </p>
                </div>
              </div>
              {!isDownloading && (
                <button
                  onClick={handleCloseModal}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                  aria-label="Close modal"
                >
                  <Icon name="X" size={20} />
                </button>
              )}
            </div>

            {!isDownloading ? (
              <>
                <div className="bg-muted rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Project Size</span>
                    <span className="text-sm text-muted-foreground">{projectSize}</span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Total Files</span>
                    <span className="text-sm text-muted-foreground">{fileCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Format</span>
                    <span className="text-sm text-muted-foreground">ZIP Archive</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    fullWidth
                    iconName="Download"
                    iconPosition="left"
                    onClick={handleConfirmDownload}
                  >
                    Confirm Download
                  </Button>
                </div>
              </>
            ) : (
              <div className="py-8">
                <div className="flex flex-col items-center gap-4">
                  <div className="animate-spin">
                    <Icon name="Loader2" size={48} className="text-primary" />
                  </div>
                  <div className="w-full">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium">Preparing download...</span>
                      <span className="text-sm text-muted-foreground">{downloadProgress}%</span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${downloadProgress}%` }}
                      />
                    </div>
                  </div>
                  {downloadProgress === 100 && (
                    <div className="flex items-center gap-2 text-success">
                      <Icon name="CheckCircle2" size={20} />
                      <span className="text-sm font-medium">Download complete!</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DownloadActionPanel;